/**
 * Offline retry queue for voucher redemptions.
 *
 * When the /redeem page tries to submit a redemption and the network is
 * unavailable, the payload is enqueued here and retried automatically when
 * connectivity returns or manually via a banner button.
 *
 * Audit-imposed bounds:
 *   - 24h TTL (entries older than this are dropped on prune)
 *   - Max 5 retries per entry — after that, status becomes 'failed' and
 *     the entry is held for the user to inspect/dismiss
 *   - Queue cap of 25 entries — when full, new enqueues are rejected
 *     (the user gets an explicit error rather than silent loss)
 *   - Each entry has a stable local id (crypto.randomUUID()) so the
 *     banner UI can render and key on it
 *   - Status: 'pending' | 'retrying' | 'failed'
 *
 * "Already redeemed" responses on retry are treated as success-with-note:
 * the prior submission probably did go through, so we remove the queue
 * entry and surface a toast rather than a hard failure.
 *
 * Storage: localStorage. Single key holds the JSON-serialized array. We
 * read-modify-write atomically per operation (acceptable for a single-tab
 * desk-bound retailer portal; not suitable for cross-tab coordination).
 */

import { redeemVoucher, type RedeemVoucherInput } from "./redemption.service";

const STORAGE_KEY = "voucher_retry_queue_v1";
const TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 5;
const QUEUE_CAP = 25;

export type QueueEntryStatus = "pending" | "retrying" | "failed";

/**
 * Snapshot of just enough page state to render the success screen after a
 * queued redemption resolves. The success render needs to know whether
 * this was an event voucher (label switches between "Vendor Redemption #" and
 * "Redeemed By") plus the discount amount for currency formatting; both
 * are only known at submit time and are gone if the user reloads while
 * offline. We persist them with the queue entry so reconnect → success
 * is a faithful UX of "what would have happened if the submit had gone
 * through immediately."
 */
export interface QueuedRedemptionDisplayContext {
  isEventVoucher: boolean;
  discountValue: number;
}

export interface QueuedRedemption {
  id: string;
  payload: RedeemVoucherInput;
  enqueuedAt: number;
  lastAttemptAt: number | null;
  attemptCount: number;
  status: QueueEntryStatus;
  lastError?: string;
  /**
   * Display label for the banner — voucher_code uppercase. We persist this
   * separately from payload.voucher_code so we don't have to dig into the
   * payload to render and so the queue stays self-describing.
   */
  voucherCode: string;
  /**
   * Snapshot used to restore the success page after a successful retry.
   * Optional only for backward compatibility with entries written by an
   * older build; new enqueues always include it.
   */
  displayContext?: QueuedRedemptionDisplayContext;
}

export interface ProcessOutcome {
  processed: number;
  succeeded: number;
  alreadyRedeemed: number;
  businessFailures: number;
  networkFailures: number;
  failed: number; // attempts >= MAX_ATTEMPTS
  remaining: number;
}

function readRaw(): QueuedRedemption[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as QueuedRedemption[];
  } catch {
    return [];
  }
}

function writeRaw(entries: QueuedRedemption[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (err) {
    console.warn("Failed to persist redemption queue:", err);
  }
}

/**
 * Drop entries older than TTL_MS. Called on every read so callers always
 * see a current view.
 */
function pruneExpired(entries: QueuedRedemption[]): QueuedRedemption[] {
  const cutoff = Date.now() - TTL_MS;
  return entries.filter((e) => e.enqueuedAt >= cutoff);
}

export function getQueue(): QueuedRedemption[] {
  const pruned = pruneExpired(readRaw());
  // Persist the prune so we don't repeat work on next read.
  writeRaw(pruned);
  return pruned;
}

export function getQueueCount(): number {
  return getQueue().length;
}

/**
 * Add a new redemption to the queue. Returns the new entry on success,
 * or null if the queue is at cap.
 */
export function enqueue(
  payload: RedeemVoucherInput,
  displayContext?: QueuedRedemptionDisplayContext,
): QueuedRedemption | null {
  const entries = pruneExpired(readRaw());
  if (entries.length >= QUEUE_CAP) {
    return null;
  }
  const entry: QueuedRedemption = {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    payload,
    enqueuedAt: Date.now(),
    lastAttemptAt: null,
    attemptCount: 0,
    status: "pending",
    voucherCode: (payload.voucher_code || "").toUpperCase(),
    displayContext,
  };
  entries.push(entry);
  writeRaw(entries);
  return entry;
}

export function removeEntry(id: string): void {
  const entries = readRaw().filter((e) => e.id !== id);
  writeRaw(entries);
}

export function clearAll(): void {
  writeRaw([]);
}

export interface QueuedSuccessDetail {
  /** Voucher code (uppercase). */
  voucherCode: string;
  /** Amount applied (may differ from the voucher's discount value). */
  appliedAmount: number;
  /** Vendor Redemption # for event vouchers, redeemed_by otherwise. */
  redeemedBy: string;
  /** Snapshot stored at enqueue time. */
  displayContext?: QueuedRedemptionDisplayContext;
}

export interface ProcessHooks {
  /**
   * Called when a queued redemption fully succeeds. Detail carries
   * everything the page needs to render the success screen — the page
   * stash this and switch to its post-redemption confirmation view.
   */
  onSuccess?: (entry: QueuedRedemption, detail: QueuedSuccessDetail) => void;
  /**
   * Called when a queued redemption resolves to "already redeemed" — treated
   * as success-with-note. The prior attempt likely went through.
   */
  onAlreadyRedeemed?: (entry: QueuedRedemption) => void;
  /** Called when a queued redemption is rejected by a business rule. */
  onBusinessFailure?: (entry: QueuedRedemption, message: string) => void;
  /**
   * Called when an entry has exhausted its retries and moved to 'failed'
   * status. Stays in the queue until the user dismisses it.
   */
  onMaxAttempts?: (entry: QueuedRedemption) => void;
}

/**
 * Process the queue once, attempting each pending entry sequentially.
 * Returns a summary; the caller (banner) is expected to refresh its view
 * by calling getQueue() afterwards.
 *
 * Sequential — not parallel — to avoid thundering-herd when reconnecting
 * and to keep error attribution simple.
 */
export async function processQueue(
  hooks: ProcessHooks = {},
): Promise<ProcessOutcome> {
  const outcome: ProcessOutcome = {
    processed: 0,
    succeeded: 0,
    alreadyRedeemed: 0,
    businessFailures: 0,
    networkFailures: 0,
    failed: 0,
    remaining: 0,
  };

  let entries = pruneExpired(readRaw());
  // Snapshot pending entries so concurrent mutations don't surprise us.
  const pending = entries.filter(
    (e) => e.status === "pending" || e.status === "retrying",
  );

  for (const entry of pending) {
    outcome.processed++;
    // Mark as retrying so the UI can render a spinner.
    entries = readRaw().map((e) =>
      e.id === entry.id ? { ...e, status: "retrying" as QueueEntryStatus } : e,
    );
    writeRaw(entries);

    let result;
    try {
      result = await redeemVoucher(entry.payload);
    } catch (err) {
      result = {
        success: false,
        failureKind: "network" as const,
        message: err instanceof Error ? err.message : "Unknown error",
      };
    }

    const now = Date.now();

    if (result.success) {
      outcome.succeeded++;
      // Hook BEFORE removal so the entry is still in the snapshot for UI.
      const isEvent = entry.displayContext?.isEventVoucher ?? false;
      const redeemedByLabel = isEvent
        ? entry.payload.vendor_name || entry.payload.booth_id || ""
        : entry.payload.redeemed_by || "";
      hooks.onSuccess?.(entry, {
        voucherCode: entry.voucherCode,
        appliedAmount:
          result.appliedAmount ??
          entry.payload.applied_amount ??
          entry.displayContext?.discountValue ??
          0,
        redeemedBy: redeemedByLabel,
        displayContext: entry.displayContext,
      });
      const after = readRaw().filter((e) => e.id !== entry.id);
      writeRaw(after);
      continue;
    }

    if (result.alreadyRedeemed) {
      outcome.alreadyRedeemed++;
      hooks.onAlreadyRedeemed?.(entry);
      const after = readRaw().filter((e) => e.id !== entry.id);
      writeRaw(after);
      continue;
    }

    if (result.failureKind === "business") {
      outcome.businessFailures++;
      hooks.onBusinessFailure?.(entry, result.message);
      // Business rejection — won't succeed on retry. Mark failed and keep
      // in queue for user inspection.
      const after = readRaw().map((e) =>
        e.id === entry.id
          ? {
              ...e,
              status: "failed" as QueueEntryStatus,
              lastAttemptAt: now,
              lastError: result.message,
            }
          : e,
      );
      writeRaw(after);
      continue;
    }

    // Network failure path — bump attemptCount and either retry-pending or
    // mark failed if exhausted.
    outcome.networkFailures++;
    const newAttempt = entry.attemptCount + 1;
    if (newAttempt >= MAX_ATTEMPTS) {
      outcome.failed++;
      hooks.onMaxAttempts?.(entry);
      const after = readRaw().map((e) =>
        e.id === entry.id
          ? {
              ...e,
              status: "failed" as QueueEntryStatus,
              attemptCount: newAttempt,
              lastAttemptAt: now,
              lastError: result.message,
            }
          : e,
      );
      writeRaw(after);
    } else {
      const after = readRaw().map((e) =>
        e.id === entry.id
          ? {
              ...e,
              status: "pending" as QueueEntryStatus,
              attemptCount: newAttempt,
              lastAttemptAt: now,
              lastError: result.message,
            }
          : e,
      );
      writeRaw(after);
    }
  }

  outcome.remaining = readRaw().length;
  return outcome;
}

/**
 * Bounds exposed for tests + UI display ("up to 25 redemptions, 24h TTL").
 */
export const QUEUE_BOUNDS = {
  TTL_MS,
  MAX_ATTEMPTS,
  QUEUE_CAP,
};
